import { useEffect, useMemo, useRef } from "react";

import { renderMarketingMarkup, type MarketingVariant } from "./content";
import { initializeApprovedMotion } from "./motion.client";

export type { MarketingVariant } from "./content";

export type MarketingPageProps = {
  variant: MarketingVariant;
  userType?: string | null;
};

type Action = { href: string; label: string };

function actionsFor(userType?: string | null): { primary: Action; secondary: Action } {
  if (userType === "evaluator") {
    return { primary: { href: "/project-score", label: "평가 참여" }, secondary: { href: "/evaluator", label: "나의 평가" } };
  }
  if (userType === "admin" || userType === "organization" || userType === "operator") {
    return { primary: { href: "/project-manage/new", label: "프로젝트 만들기" }, secondary: { href: "/dashboard", label: "대시보드" } };
  }
  return { primary: { href: "/join", label: "회원가입" }, secondary: { href: "/login", label: "로그인" } };
}

function initializeNavigation(scope: HTMLElement) {
  const header = scope.querySelector<HTMLElement>(".site-header");
  const nav = scope.querySelector<HTMLElement>("#primary-nav");
  const toggle = scope.querySelector<HTMLButtonElement>(".menu-toggle");
  if (!header || !nav || !toggle) return () => undefined;
  header.classList.add("nav-ready");
  toggle.hidden = false;
  const close = (restoreFocus = false) => {
    nav.classList.remove("is-open");
    toggle.setAttribute("aria-expanded", "false");
    if (restoreFocus) toggle.focus();
  };
  const onToggle = () => {
    const open = toggle.getAttribute("aria-expanded") !== "true";
    nav.classList.toggle("is-open", open);
    toggle.setAttribute("aria-expanded", String(open));
  };
  const onLinkClick = () => close();
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") close(true);
  };
  const onOutsideClick = (event: MouseEvent) => {
    if (!header.contains(event.target as Node)) close();
  };
  const desktop = window.matchMedia("(min-width: 1021px)");
  const onDesktopChange = () => close();
  const links = [...nav.querySelectorAll("a")];
  toggle.addEventListener("click", onToggle);
  links.forEach((link) => link.addEventListener("click", onLinkClick));
  header.addEventListener("keydown", onKeyDown);
  document.addEventListener("click", onOutsideClick);
  desktop.addEventListener?.("change", onDesktopChange);
  return () => {
    toggle.removeEventListener("click", onToggle);
    links.forEach((link) => link.removeEventListener("click", onLinkClick));
    header.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("click", onOutsideClick);
    desktop.removeEventListener?.("change", onDesktopChange);
  };
}

function initializeJourneyTabs(scope: HTMLElement) {
  const tablist = scope.querySelector<HTMLElement>(".journey-tabs");
  const panelsShell = scope.querySelector<HTMLElement>(".journey-panels");
  if (!tablist || !panelsShell) return () => undefined;
  const tabs = [...tablist.querySelectorAll<HTMLButtonElement>("[role=tab]")];
  const panels = tabs.map((tab) => scope.querySelector<HTMLElement>("#" + tab.getAttribute("aria-controls")));
  if (!tabs.length || panels.some((panel) => !panel)) return () => undefined;
  const select = (index: number, focus = false) => {
    tabs.forEach((tab, position) => {
      const active = position === index;
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
      panels[position]!.hidden = !active;
      panels[position]!.classList.toggle("is-entering", active);
    });
    if (focus) tabs[index]?.focus();
  };
  const cleanups = tabs.map((tab, index) => {
    const onClick = () => select(index);
    const onKeyDown = (event: KeyboardEvent) => {
      const next = event.key === "ArrowRight" ? (index + 1) % tabs.length : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : undefined;
      if (next !== undefined) { event.preventDefault(); select(next, true); }
    };
    tab.addEventListener("click", onClick);
    tab.addEventListener("keydown", onKeyDown);
    return () => { tab.removeEventListener("click", onClick); tab.removeEventListener("keydown", onKeyDown); };
  });
  tablist.hidden = false;
  panelsShell.classList.add("tabs-ready");
  select(0);
  return () => cleanups.forEach((cleanup) => cleanup());
}

export function MarketingPage({ variant, userType = null }: MarketingPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const markup = useMemo(() => {
    const actions = actionsFor(userType);
    return renderMarketingMarkup(variant, actions.primary, actions.secondary);
  }, [userType, variant]);
  useEffect(() => {
    const scope = containerRef.current;
    if (!scope) return;
    const navigation = initializeNavigation(scope);
    const tabs = variant === "recommended" ? initializeJourneyTabs(scope) : () => undefined;
    const motion = variant === "recommended" ? initializeApprovedMotion(scope) : () => undefined;
    return () => { motion(); tabs(); navigation(); };
  }, [markup, variant]);
  return <div ref={containerRef} className={"marketing-page marketing-" + variant} dangerouslySetInnerHTML={{ __html: markup }} />;
}

export default MarketingPage;
